import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { H3, TypographyWeight } from '../../style/Typography';
import * as S from './styled';

const CollapsibleContent = ({
  title,
  titleIcon,
  isAlwaysExpanded,
  onExpand,
  isExpanded: propExpanded,
  isOpenByDefault,
  extraComponent,
  className,
  children,
  testId,
  disabled = false,
  shouldUnmount = false
}) => {
  const [stateExpanded, setIsExpanded] = useState(propExpanded || isAlwaysExpanded || isOpenByDefault);
  const isExpanded = typeof propExpanded === 'boolean' ? propExpanded : stateExpanded;
  const [shouldRenderContent, setShouldRenderContent] = useState(isExpanded);

  const handleExpandClick = () => {
    if (disabled || isAlwaysExpanded) return;
    const newExpandedState = !isExpanded;

    if (shouldUnmount && newExpandedState) {
      setShouldRenderContent(true);
    }

    setIsExpanded(newExpandedState);

    if (onExpand) {
      onExpand(newExpandedState);
    }
  };

  const handleAnimationComplete = () => {
    if (!isExpanded && shouldUnmount) {
      setShouldRenderContent(false);
    }
  };

  return (
    <S.Wrapper className={className}>
      <S.CollapsibleSection
        $isAlwaysExpanded={isAlwaysExpanded}
        disabled={disabled}
        onClick={handleExpandClick}
        data-testid={testId || 'collapsible-section'}>
        <S.Title>
          {!isAlwaysExpanded && (
            <S.ArrowWrapper
              initial={S.CollapsedArrowVariants.expanded}
              animate={isExpanded ? 'expanded' : 'collapsed'}
              variants={S.CollapsedArrowVariants}>
              <S.ChevronIcon disabled={disabled} />
            </S.ArrowWrapper>
          )}
          {typeof title === 'string' ? <H3 weight={TypographyWeight.BOLD}>{title}</H3> : title}
          {titleIcon}
        </S.Title>
        {extraComponent && <S.Extra>{extraComponent}</S.Extra>}
      </S.CollapsibleSection>
      <S.ExpandedSection
        $expanded={isExpanded}
        data-testid={testId ? `${testId}-content` : 'expanded-content-section'}
        initial={{ height: 0, opacity: 0 }}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0
        }}
        transition={{ duration: 0.3, ease: 'easeInOut' }}
        onAnimationComplete={handleAnimationComplete}>
        {(!shouldUnmount || shouldRenderContent) && <S.ChildWrapper>{children}</S.ChildWrapper>}
      </S.ExpandedSection>
    </S.Wrapper>
  );
};

CollapsibleContent.propTypes = {
  children: PropTypes.node.isRequired,
  extraComponent: PropTypes.node,
  title: PropTypes.node,
  titleIcon: PropTypes.node,
  className: PropTypes.string,
  testId: PropTypes.string,
  onExpand: PropTypes.func,
  isExpanded: PropTypes.bool,
  isAlwaysExpanded: PropTypes.bool,
  isOpenByDefault: PropTypes.bool,
  disabled: PropTypes.bool,
  shouldUnmount: PropTypes.bool
};

export default CollapsibleContent;
